import { BreedInterestController, PreloadController, SeoController } from '@application/components';
import { useNewsletter, useRouter } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import type { PopularBreedData } from '@presentation/pages';
import { HomeLandingPage } from '@presentation/pages';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useFormatLitters } from '../LitterListPageController/api/useFormatLitters';
import { useFormatBreeders } from './api/useFormatBreeders';
import { useGetAdditionalPopularBreeds } from './api/useGetAdditionalPopularBreeds';
import { useGetBreeders } from './api/useGetBreeders';
import { useGetLitters } from './api/useGetLitters';
import { useGetPopularBreeds } from './api/useGetPopularBreeds';

export const HomeLandingPageController: FC = () => {
  const { routeLocationDescriptor, navigateToRoute, translate, countryCode } = useRouter();
  const { litters, littersLoading } = useGetLitters();
  const { breeders, breedersLoading } = useGetBreeders();
  const { formattedLitters, imagesToPreload } = useFormatLitters(litters);
  const { formattedBreeders } = useFormatBreeders(breeders);
  const { newsletterProps } = useNewsletter();
  const { breeds } = useGetPopularBreeds();
  const { breeds: additionalBreeds } = useGetAdditionalPopularBreeds();
  const [showMoreLitterCategories, setShowMoreLitterCategories] = useState(false);
  const [showMoreBreederCategories, setShowMoreBreederCategories] = useState(false);

  const popularBreederCategoriesByBreed = useMemo(() => {
    return {
      items: breeds.map((b) => ({
        name: b.translations[0].name,
        image: b.imgs[0].small,
        link: `${routeLocationDescriptor('breederList').pathname}${b.name}`,
      })),
      moreItems: additionalBreeds.map((b) => ({
        name: translate('homeLanding.breederCategories.link').replace(
          '$breed',
          b.translations[0].name ?? '',
        ),
        link: `${routeLocationDescriptor('breederList').pathname}${b.name}`,
      })),
      showMore: showMoreBreederCategories,
      onShowMore: () => {
        if ([CountryCode.NL, CountryCode.DE, CountryCode.EU].includes(countryCode)) {
          navigateToRoute('breederLinks');
        } else {
          setShowMoreBreederCategories(true);
        }
      },
    } as PopularBreedData;
  }, [
    additionalBreeds,
    breeds,
    countryCode,
    navigateToRoute,
    routeLocationDescriptor,
    showMoreBreederCategories,
    translate,
  ]);

  const popularLitterCategoriesByBreed = useMemo(() => {
    return {
      items: breeds.map((b) => ({
        name: b.translations[0].name,
        image: b.imgs[0].small,
        link: `${routeLocationDescriptor('litterList').pathname}${b.name}`,
      })),
      moreItems: additionalBreeds.map((b) => ({
        name: translate('homeLanding.litterCategories.link').replace(
          '$breed',
          b.translations[0].name ?? '',
        ),
        link: `${routeLocationDescriptor('litterList').pathname}${b.name}`,
      })),
      showMore: showMoreLitterCategories,
      onShowMore: () => {
        if ([CountryCode.NL, CountryCode.DE, CountryCode.EU].includes(countryCode)) {
          navigateToRoute('litterLinks');
        } else {
          setShowMoreLitterCategories(true);
        }
      },
    } as PopularBreedData;
  }, [
    additionalBreeds,
    breeds,
    countryCode,
    navigateToRoute,
    routeLocationDescriptor,
    showMoreLitterCategories,
    translate,
  ]);

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={translate('homeLanding.seo.title')}
        description={translate('homeLanding.seo.description')}
        keywords={translate('homeLanding.seo.keywords')}
      />
      <HomeLandingPage
        litterItems={formattedLitters}
        breederItems={formattedBreeders}
        litterItemsLoading={littersLoading}
        onStoryClick={() => navigateToRoute('story')}
        breederItemsLoading={breedersLoading}
        onContactClick={() => navigateToRoute('contact')}
        onMoreBreedersclick={() => navigateToRoute('breederList')}
        onMoreLittersClick={() => navigateToRoute('litterList')}
        onBuyClick={() => navigateToRoute('litterList')}
        onSellClick={() => navigateToRoute('breederLanding')}
        joinBanner={{
          buyerLink: routeLocationDescriptor('joinBuyer'),
          breederLink: routeLocationDescriptor('joinBreeder'),
          shelterLink: routeLocationDescriptor('joinShelter'),
          vetLink: routeLocationDescriptor('joinVet'),
          groomerLink: routeLocationDescriptor('joinGroomer'),
          educatorLink: routeLocationDescriptor('joinEducator'),
        }}
        promoNewletter={newsletterProps}
        popularLitterCategoriesByBreed={popularLitterCategoriesByBreed}
        popularBreederCategoriesByBreed={popularBreederCategoriesByBreed}
        breedInterestBanner={
          countryCode !== CountryCode.BE ? <BreedInterestController /> : undefined
        }
      />
    </>
  );
};
